import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play } from 'lucide-react';
import type { LibraryVideo } from '../../types';
import { VideoDetailModal } from './VideoDetailModal';

const GENRES = ['Pop', 'Hip-Hop', 'Rock', 'Electronic', 'R&B', 'Country', 'Indie', 'Alternative', 'Metal', 'Schlager'];
const CATEGORIES = ['Performance', 'Relatable', 'Entertainment'];
const VIDEOS_PER_PAGE = 28;

interface GalleryViewProps {
  videos: LibraryVideo[];
  isPublicMode?: boolean;
  isLoading?: boolean;
}

export const GalleryView: React.FC<GalleryViewProps> = ({ videos, isPublicMode = false, isLoading = false }) => {
  const [filteredVideos, setFilteredVideos] = useState<LibraryVideo[]>(videos);
  const [displayedVideos, setDisplayedVideos] = useState<LibraryVideo[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<LibraryVideo | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingRef = useRef(false);

  // Apply filters
  useEffect(() => {
    let filtered = videos;

    if (selectedGenres.length > 0) {
      filtered = filtered.filter((video) => {
        if (Array.isArray(video.genre)) {
          return video.genre.some((g: string) => selectedGenres.includes(g));
        }
        return typeof video.genre === 'string' && selectedGenres.includes(video.genre);
      });
    }

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((video) => {
        if (Array.isArray(video.category)) {
          return video.category.some((c: string) => selectedCategories.includes(c));
        }
        return typeof video.category === 'string' && selectedCategories.includes(video.category);
      });
    }

    setFilteredVideos(filtered);
    setDisplayedVideos(filtered.slice(0, VIDEOS_PER_PAGE));
  }, [selectedGenres, selectedCategories, videos]);

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    );
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const clearFilters = () => {
    setSelectedGenres([]);
    setSelectedCategories([]);
  };

  // Load more videos
  const loadMoreVideos = useCallback(() => {
    if (loadingRef.current || isLoadingMore) return;

    setDisplayedVideos(prev => {
      if (prev.length >= filteredVideos.length) return prev;

      const nextBatch = filteredVideos.slice(prev.length, prev.length + VIDEOS_PER_PAGE);
      if (nextBatch.length === 0) return prev;

      loadingRef.current = true;
      setIsLoadingMore(true);

      setTimeout(() => {
        loadingRef.current = false;
        setIsLoadingMore(false);
      }, 300);

      return [...prev, ...nextBatch];
    });
  }, [filteredVideos, isLoadingMore]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (loadingRef.current || isLoadingMore) return;

      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        if (displayedVideos.length < filteredVideos.length) {
          loadMoreVideos();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [displayedVideos.length, filteredVideos.length, isLoadingMore, loadMoreVideos]);

  const activeFilterCount = selectedGenres.length + selectedCategories.length;

  return (
    <div className="text-white min-h-screen" style={{ backgroundColor: '#000000' }}>
      {/* Filter Bar */}
      <div className={`pb-4 relative z-[200] ${selectedVideo ? 'hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 pt-12 pb-4">
          {/* Library Headline */}
          <h1 className="text-4xl font-bold text-center mb-8">Library</h1>

          <div className="flex justify-center">
            {/* Filter Button */}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`p-3 flex items-center gap-2 transition relative cursor-pointer ${
                showFilters ? 'text-[#0000fe]' : 'text-white hover:text-[#0000fe]'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"></path>
              </svg>
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#0000fe] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 p-6 bg-black/80 rounded-lg">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Genre Filter */}
                <div>
                  <h3 className="font-bold mb-3 uppercase tracking-wider text-sm">Genre</h3>
                  <div className="flex flex-wrap gap-2">
                    {GENRES.map(genre => (
                      <button
                        key={genre}
                        onClick={() => toggleGenre(genre)}
                        className={`px-4 py-2 border transition text-sm ${
                          selectedGenres.includes(genre)
                            ? 'bg-[#0000fe] border-[#0000fe] text-white'
                            : 'border-white/20 text-white/70 hover:bg-white/10'
                        }`}
                      >
                        {genre}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Category Filter */}
                <div>
                  <h3 className="font-bold mb-3 uppercase tracking-wider text-sm">Content Type</h3>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map(category => (
                      <button
                        key={category}
                        onClick={() => toggleCategory(category)}
                        className={`px-4 py-2 border transition text-sm ${
                          selectedCategories.includes(category)
                            ? 'bg-[#0000fe] border-[#0000fe] text-white'
                            : 'border-white/20 text-white/70 hover:bg-white/10'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-sm text-gray-400 hover:text-white transition"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Video Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        {isLoading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-[#0000fe] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : displayedVideos.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-400 mb-4">No videos found</p>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-[#0000fe] hover:underline"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {displayedVideos.map(video => (
                <div
                  key={video.id}
                  className="border-2 border-white/20 hover:border-[#0000fe] transition group cursor-pointer"
                  onClick={() => setSelectedVideo(video)}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-[9/16] bg-gray-900">
                    {(video.thumbnailStorageUrl || video.thumbnailUrl) ? (
                      <img
                        src={video.thumbnailStorageUrl || video.thumbnailUrl}
                        alt={video.title || 'Video'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-16 h-16 text-gray-600" />
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <div className="bg-[#0000fe] rounded-full p-4">
                        <Play className="w-8 h-8 text-white" fill="white" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Loading More Indicator */}
            {isLoadingMore && (
              <div className="text-center py-8">
                <div className="inline-block w-8 h-8 border-4 border-[#0000fe] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* End of Results */}
            {displayedVideos.length >= filteredVideos.length && filteredVideos.length > 0 && (
              <div className="text-center py-8 text-gray-400">
                All videos loaded ({filteredVideos.length})
              </div>
            )}
          </>
        )}
      </div>

      {/* Video Detail Modal */}
      {selectedVideo && (
        <VideoDetailModal
          video={selectedVideo}
          isOpen={!!selectedVideo}
          onClose={() => setSelectedVideo(null)}
          onUpdate={() => {}}
          canEdit={false}
        />
      )}
    </div>
  );
};
